// Utility functions for handling date parsing and formatting

/**
 * Safely parse a datetime string that may be in various formats
 * Handles formats like "2025-05-15 03:29:10.929" or ISO format strings
 */
export const parseDateSafely = (dateString: string): Date => {
  if (!dateString) {
    return new Date();
  }

  // Try to parse the date string as-is first (handles ISO format)
  let parsedDate = new Date(dateString);
  
  // If it's valid, return it
  if (!isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  // If the date string is in format "YYYY-MM-DD HH:mm:ss.SSS" (without T or timezone)
  // Convert it to ISO 8601 format for consistent parsing
  const dateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?$/;
  
  if (dateRegex.test(dateString)) {
    // Replace the space with 'T' and add 'Z' for UTC timezone
    const isoString = dateString.replace(' ', 'T') + 'Z';
    parsedDate = new Date(isoString);
    
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  // If all parsing attempts fail, log warning and return current date
  console.warn(`Invalid date string: ${dateString}`);
  return new Date(); // Return current date as fallback
};

/**
 * Format a date string to a localized date string
 */
export const formatDateString = (dateString: string): string => {
  try {
    const date = parseDateSafely(dateString);
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format a date string to a localized date and time string
 */
export const formatDateTimeString = (dateString: string): string => {
  try {
    const date = parseDateSafely(dateString);
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid Date';
  }
};

/**
 * Format a date string smartly:
 * - If today: show only time (e.g., "2:30 PM")
 * - If other days: show date and time (e.g., "Jan 15, 2025 at 2:30 PM")
 */
export const formatSmartDateTime = (dateString: string): string => {
  try {
    const date = parseDateSafely(dateString);
    const now = new Date();
    
    // Check if the date is today
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      // Show only time for today
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      // Show date and time for other days
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) + ' at ' + date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting smart datetime:', error);
    return 'Invalid Date';
  }
};
